import * as Sentry from '@sentry/react'
import type { DefaultError } from '@tanstack/react-query'
import { ApiError } from './api-client/api-error'

// 0 is the status code for hidden redirects (e.g. unauthenticated requests)
const AUTH_ERROR_STATUS_CODES = [499, 401, 302, 0]
const EXPECTED_ERROR_STATUS_CODES = [499, 401]
const FORBIDDEN_ERROR_STATUS_CODE = 403
const REJECTED_CREDENTIALS_ERROR_STATUS_CODE = 424

export function isAuthError(error: unknown) {
  return error instanceof ApiError && AUTH_ERROR_STATUS_CODES.includes(error.status)
}

export function isExpectedError(error: unknown) {
  return error instanceof ApiError && EXPECTED_ERROR_STATUS_CODES.includes(error.status)
}

export function isForbiddenError(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === FORBIDDEN_ERROR_STATUS_CODE
}

/**
 * A 424 (the vendor refused the stored credentials, or they lack a needed permission) is a
 * documented answer an integration picker shows inline: it does not recover on a retry, and it is
 * not a fault to report.
 */
export function isRejectedCredentialsError(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === REJECTED_CREDENTIALS_ERROR_STATUS_CODE
}

/** The client-wide query retry policy, named so a query that overrides `retry` can extend it. */
export function shouldRetryQuery(failureCount: number, error: unknown) {
  return !isForbiddenError(error) && !isAuthError(error) && failureCount < 2
}

/** The client-wide query `throwOnError` policy, named so a query that overrides it can extend it. */
export function shouldThrowQueryError(error: unknown) {
  if (!isExpectedError(error)) {
    Sentry.captureException(error)
  }
  return isAuthError(error)
}

/**
 * The client-wide `retry` and `throwOnError` policies for a query that shows some failures inline
 * as its answer: those are neither retried nor thrown, and every other failure follows the
 * client-wide policy.
 */
export function inlineAnswerQueryPolicy(isInlineAnswer: (error: unknown) => boolean) {
  return {
    retry: (failureCount: number, error: DefaultError) =>
      !isInlineAnswer(error) && shouldRetryQuery(failureCount, error),
    throwOnError: (error: DefaultError) => !isInlineAnswer(error) && shouldThrowQueryError(error),
  }
}
